import React, { createContext, useContext, useEffect, useState } from 'react';
import api from './api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('mjtech_token');
    const userData = localStorage.getItem('mjtech_user');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        api.defaults.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem('mjtech_token');
        localStorage.removeItem('mjtech_user');
      }
    }

    setLoading(false);
  }, []);

  const signIn = async (username, password) => {
    try {
      const response = await api.post('/auth/login', {
        Username: username.toLowerCase().trim(), // Using PascalCase for backend consistency, lowercase for case-insensitive login
        Password: password,
      });

      const { User: userData, Token: access_token } = response.data;

      // Store user and token
      setUser(userData);
      localStorage.setItem('mjtech_token', access_token);
      localStorage.setItem('mjtech_user', JSON.stringify(userData));
      api.defaults.headers.Authorization = `Bearer ${access_token}`;

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const signUp = async (email, password, fullName) => {
    try {
      const response = await api.post('/auth/register', {
        Email: email,
        Password: password,
        FullName: fullName,
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Message || 'Registration failed');
    }
  };

  const signOut = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Even if logout fails on server, clear local data
      console.warn('Logout request failed:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('mjtech_token');
      localStorage.removeItem('mjtech_user');
      delete api.defaults.headers.Authorization;
    }
  };

  const isAdmin = () => {
    return user?.Role === 'admin';
  };

  const isManager = () => {
    return user?.Role === 'manager';
  };

  const isEmployee = () => {
    return user?.Role === 'employee';
  };

  const isViewer = () => {
    return user?.Role === 'viewer';
  };

  const canCreate = () => {
    return ['admin', 'manager', 'employee'].includes(user?.Role);
  };

  const canEdit = () => {
    return ['admin', 'manager', 'employee'].includes(user?.Role);
  };

  const canDelete = () => {
    return ['admin', 'manager'].includes(user?.Role);
  };

  const canManageUsers = () => {
    return user?.Role === 'admin';
  };

  const canRefundSales = () => {
    const canRefund = ['admin', 'manager'].includes(user?.Role);
    // Debug logging (reduced frequency)
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.01) { // Only log 1% of the time
      console.log('canRefundSales debug:', {
        userRole: user?.Role,
        canRefund: canRefund
      });
    }
    return canRefund;
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/auth/change-password', {
        CurrentPassword: currentPassword,
        NewPassword: newPassword,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Password change failed');
    }
  };

  const registerUser = async (userData) => {
    try {
      const response = await api.post('/auth/register', {
        Username: userData.username.toLowerCase().trim(),
        Email: userData.email.toLowerCase().trim(),
        Password: userData.password,
        FullName: userData.fullName,
        Role: userData.role,
        BranchId: userData.branchId || null,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Registration failed');
    }
  };

  const getUsers = async () => {
    try {
      const response = await api.get('/auth/users');
      return response.data.Users;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Failed to fetch users');
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      const response = await api.put(`/auth/users/${userId}/role`, {
        Role: role,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Failed to update user role');
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await api.delete(`/auth/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Failed to delete user');
    }
  };

  const bulkDeleteProductInventory = async (confirmation) => {
    try {
      const response = await api.post('/api/v1/admin/bulk-delete-product-inventory', { confirmation });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Failed to delete product inventory data');
    }
  };

  const bulkDeleteQuaipoLookup = async (confirmation) => {
    try {
      const response = await api.post('/api/v1/admin/bulk-delete-quaipo-lookup', { confirmation });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Failed to delete quaipo lookup data');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', {
        Username: profileData.username.toLowerCase().trim(),
        Email: profileData.email.toLowerCase().trim(),
        FullName: profileData.fullName,
      });

      // Update the user state with new data
      if (response.data.User) {
        setUser(response.data.User);
        localStorage.setItem('mjtech_user', JSON.stringify(response.data.User));
      }

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.Error || 'Failed to update profile');
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    isAdmin,
    isManager,
    isEmployee,
    isViewer,
    canCreate,
    canEdit,
    canDelete,
    canManageUsers,
    canRefundSales,
    changePassword,
    registerUser,
    getUsers,
    updateUserRole,
    deleteUser,
    bulkDeleteProductInventory,
    bulkDeleteQuaipoLookup,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};